import { useState, useMemo } from 'react';
import { X } from '@/components/icons';
import { SearchInput } from './ui/search-input';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { getCountryDataList } from 'countries-list';
import { CountryFlag } from './ui/country-flag';

interface NationalityPickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (nationality: string) => void;
  selectedNationality?: string;
}

export function NationalityPickerDrawer({
  isOpen,
  onClose,
  onSelect,
  selectedNationality,
}: NationalityPickerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleSelect = (nationality: string) => {
    onSelect(nationality);
    handleClose();
  };

  const filteredCountries = useMemo(() =>
    getCountryDataList()
      .filter((country) =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name)),
    [searchQuery]
  );

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
        <DrawerHeader className='flex justify-between'>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
          <DrawerTitle>Select Nationality</DrawerTitle>
          <div className="w-10" />
        </DrawerHeader>

        <div className="px-4 pt-3 pb-3 border-b border-gray-200">
          <SearchInput
            placeholder="Search countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Country List */}
        <div className="overflow-y-auto overscroll-contain h-[400px]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="px-4 py-2">
            {filteredCountries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-gray-500">No countries found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.iso2}
                  onClick={() => handleSelect(country.name)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg ${
                    selectedNationality === country.name ? 'bg-brand-gold/10' : ''
                  }`}
                >
                  <CountryFlag iso2={country.iso2} className="w-6" />
                  <span className="text-[14px] text-text-default">{country.name}</span>
                  {selectedNationality === country.name && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-brand-gold" />
                  )}
                </button>
              ))
            )}
          </div>
          {/* Bottom spacer for safe scrolling */}
          <div className="h-4" />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
